import { gql } from '@apollo/client';
import { mutate } from '../../../../../common/graphql';
import { PageResult } from '../../../../../common/graphql/pagination';
import { GAME_BUSINESS_TIER } from '../../../../../common/graphql/games/game';
import { GAME_BUILD_FRAGMENT } from '../Game/gql/game-build-graphql';
import { BaseAdminSubmission } from '../Game/gql/submission-graphql';
import { GameJamId } from '@crazygames/gamejam/game-jam-config';
import { SUBMISSION_STATUS } from '@crazygames/game-types/src/submission/submission-status.type';
import {
  ADMIN_SUBMISSION_UPDATE_QA_RESULT_MUTATION,
  AdminSubmissionUpdateQaResultInput,
  AdminSubmissionUpdateQaResultMutation,
} from '@crazygames/graph-types/submission/admin-submission-update-qa-result-input.type';
import { DeveloperForList } from '../Developers/developers-graphql';

export const BASIC_LAUNCH_STATS_FRAGMENT = gql`
  fragment BasicLaunchStats on BasicLaunchStats {
    avgTimeOnPage
    dateFrom
    dateTo
    retentionD1
    avgConversion
  }
`;

export interface BasicLaunchAvgStatsWithDates {
  gameId: string;
  dateFrom: string;
  dateTo: string;
  avgTimeOnPage: number;
  retentionD1: number;
  avgConversion: number;
}

export interface AdminSubmissionForList extends BaseAdminSubmission {
  updatedAt: string;
  assignee?: {
    name: string;
  };
  businessAssignee?: {
    name: string;
  };
  developer: Omit<DeveloperForList, 'id'>;
  isInvited: boolean;
}

export interface ListSubmissionsQuery {
  submissions: PageResult<AdminSubmissionForList>;
}

export interface SubmissionsFilter {
  statuses: SUBMISSION_STATUS[];
  assigneeId: string | null;
  businessAssigneeId: string | null;
  businessTier?: GAME_BUSINESS_TIER;
  hasBasicLaunchEnded?: boolean;
  gameJamId?: GameJamId;
}

export const LIST_SUBMISSIONS_QUERY = gql`
  query filteredSubmissions($filter: SubmissionsFilter, $limit: Int!, $offset: Int!) {
    submissions(filter: $filter, limit: $limit, offset: $offset) {
      nodes {
        id
        gameName
        status
        developerId
        slug
        developer {
          name
          email
        }
        gameCovers {
          size
          uploadedFile {
            uploadId
            path
            size
            temporaryUrl
          }
        }
        businessAssignee {
          name
        }
        assignee {
          name
        }
        createdAt
        updatedAt
        isFullLaunch
        basicLaunchStats {
          ...BasicLaunchStats
        }
        basicLaunchExpiresOn
        statusChanges {
          status
          changedOn
          isFullLaunch
          authorName
        }
      }
      count
      pageInfo {
        hasNextPage
      }
    }
  }
  ${BASIC_LAUNCH_STATS_FRAGMENT}
`;

export const ADMIN_SUBMISSION_GAME_BUILDS_QUERY = gql`
  query AdminGetGameBuilds($id: String!) {
    submission(id: $id) {
      gameBuilds {
        ...GameBuildFragment
      }
    }
  }
  ${GAME_BUILD_FRAGMENT}
`;

export const adminSubmissionUpdateQaResult = (input: AdminSubmissionUpdateQaResultInput) =>
  mutate<AdminSubmissionUpdateQaResultInput, AdminSubmissionUpdateQaResultMutation>(ADMIN_SUBMISSION_UPDATE_QA_RESULT_MUTATION, input, {
    refetchQueries: [{ query: ADMIN_SUBMISSION_GAME_BUILDS_QUERY, variables: { id: input.id } }],
    awaitRefetchQueries: true,
  });

export interface AdminCreateSubmissionInvitationResponse {
  adminCreateSubmissionInvitation: { submissionInvitation: { id: string } };
}

export const CREATE_SUBMISSION_INVITATION_MUTATION = gql`
  mutation createInvitedSubmission {
    adminCreateSubmissionInvitation {
      submissionInvitation {
        id
      }
    }
  }
`;

export const createSubmissionInvitation = () =>
  mutate<{}, AdminCreateSubmissionInvitationResponse>(CREATE_SUBMISSION_INVITATION_MUTATION, {});
